import { useQuery } from "@tanstack/react-query";
import { salesDb } from "@/integrations/supabase/sales-client";
import { supabase } from "@/integrations/supabase/client";

export type StudentKind = "fs" | "esc" | "kn";

export type BalanceRow = {
  student_code: string;
  student_kind: StudentKind;
  event_balance: number;
  first_name?: string | null;
  last_name?: string | null;
  nickname?: string | null;
};

export const useEventBalances = () => {
  return useQuery({
    queryKey: ["event-balances"],
    queryFn: async () => {
      const { data, error } = await salesDb()
        .from("v_student_event_balance")
        .select("fishsix_student_code,esc_student_code,knoware_student_code,event_balance")
        .gt("event_balance", 0);
      if (error) throw error;
      const raw = (data ?? []) as {
        fishsix_student_code: string | null;
        esc_student_code: string | null;
        knoware_student_code: string | null;
        event_balance: number;
      }[];
      const fsCodes = raw.map((r) => r.fishsix_student_code).filter(Boolean) as string[];
      const escCodes = raw.map((r) => r.esc_student_code).filter(Boolean) as string[];
      const knCodes = raw.map((r) => r.knoware_student_code).filter(Boolean) as string[];

      const [fsRes, escRes, knRes] = await Promise.all([
        fsCodes.length
          ? (supabase as any)
              .schema("student")
              .from("fishsix_students")
              .select("code,first_name,last_name,nickname")
              .in("code", fsCodes)
          : Promise.resolve({ data: [] } as any),
        escCodes.length
          ? (supabase as any).schema("esc").from("students").select("code,name,nickname").in("code", escCodes)
          : Promise.resolve({ data: [] } as any),
        knCodes.length
          ? salesDb()
              .from("knoware_students_view")
              .select("code,student_name_th,student_nickname_th")
              .in("code", knCodes)
          : Promise.resolve({ data: [] } as any),
      ]);
      const fsMap = new Map<string, any>();
      (fsRes.data ?? []).forEach((s: any) => fsMap.set(s.code, s));
      const escMap = new Map<string, any>();
      (escRes.data ?? []).forEach((s: any) => escMap.set(s.code, s));
      const knMap = new Map<string, any>();
      (knRes.data ?? []).forEach((s: any) => knMap.set(s.code, s));

      const out: BalanceRow[] = raw
        .map((r) => {
          if (r.fishsix_student_code) {
            const s = fsMap.get(r.fishsix_student_code);
            return {
              student_code: r.fishsix_student_code,
              student_kind: "fs" as const,
              event_balance: Number(r.event_balance ?? 0),
              first_name: s?.first_name ?? null,
              last_name: s?.last_name ?? null,
              nickname: s?.nickname ?? null,
            };
          }
          if (r.esc_student_code) {
            const s = escMap.get(r.esc_student_code);
            return {
              student_code: r.esc_student_code,
              student_kind: "esc" as const,
              event_balance: Number(r.event_balance ?? 0),
              first_name: s?.name ?? null,
              last_name: null,
              nickname: s?.nickname ?? null,
            };
          }
          if (r.knoware_student_code) {
            const s = knMap.get(r.knoware_student_code);
            return {
              student_code: r.knoware_student_code,
              student_kind: "kn" as const,
              event_balance: Number(r.event_balance ?? 0),
              first_name: s?.student_name_th ?? null,
              last_name: null,
              nickname: s?.student_nickname_th ?? null,
            };
          }
          return null;
        })
        .filter(Boolean) as BalanceRow[];
      return out.sort((a, b) => b.event_balance - a.event_balance);
    },
  });
};
